import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { BadgeCheck, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const polls = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }

    const poll = async () => {
      if (polls.current >= MAX_POLLS) { setStatus("timeout"); return; }
      polls.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("error"); return; }
      } catch {}
      setTimeout(poll, 2000);
    };
    poll();
  }, [refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full bg-card border-2 border-[#6C0BA9] rounded-3xl p-8 text-center x-fade">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-[#8012C6] animate-spin mx-auto mb-4" />
            <h1 className="font-heading text-xl font-bold">Confirming your payment...</h1>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-[#6C0BA9] to-[#C084FC] flex items-center justify-center shadow-lg shadow-[#6C0BA9]/50 x-pulse mb-4">
              <BadgeCheck className="w-9 h-9 text-white" />
            </div>
            <h1 className="font-heading text-2xl font-extrabold">You're Verified! 🎉</h1>
            <p className="text-sm text-muted-foreground mt-2">Your glowing badge is now live across Xealth.</p>
            <Button data-testid="back-to-feed-btn" onClick={() => navigate("/")} className="mt-6 rounded-xl bg-[#6C0BA9] hover:bg-[#8012C6] text-white">Back to Feed</Button>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h1 className="font-heading text-xl font-bold">{status === "timeout" ? "Still processing" : "Payment issue"}</h1>
            <p className="text-sm text-muted-foreground mt-2">{status === "timeout" ? "Your badge will appear shortly if payment succeeded." : "We couldn't confirm the payment."}</p>
            <Button data-testid="back-to-feed-btn" onClick={() => navigate("/")} variant="outline" className="mt-6 rounded-xl border-border">Back to Feed</Button>
          </>
        )}
      </div>
    </div>
  );
}
